import * as React from 'react';
import { connect } from 'react-redux';

import * as Edge from '../../../core';
import { withLoadDataDefaultConfig } from '../../../components/loadData';
import errorLoadingWrapperHOC from '../../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import PlanPageBase from '../planPageBase';
import StartButton from '../startButton';
import { PlanService } from '../../../services/planService';
import CompleteEvaluation from './completeEvaluation';
import JsonDebug from '../../../components/jsonDebug';
import { CanEvalTeamsBanner } from '../../../components/teamBanner';
import { getTeamsUserCanEval } from '../../../store/permissions/selector';
import { AppState } from '../../../store';
import { setCurrentTeamFromCache } from '../../../store/team/actions';
import { ExerciseMetadataService, ExerciseMetadata } from '../../../services/exerciseMetadataService';

import { ReactComponent as ClockIcon } from '../../../assets/images/icons/Clock.svg';
import { ReactComponent as DifficultyIcon } from '../../../assets/images/icons/Difficulty.svg';
import { ReactComponent as EnvironmentIcon } from '../../../assets/images/icons/Environment.svg';
import { ReactComponent as GlassesIcon } from '../../../assets/images/icons/glasses.svg';
import { ReactComponent as LaptopIcon } from '../../../assets/images/icons/Laptop.svg';
import { ReactComponent as NoteIcon } from '../../../assets/images/icons/Note.svg';
import { ReactComponent as NotebookIcon } from '../../../assets/images/icons/Notebook.svg';
import { ReactComponent as ShieldIcon } from '../../../assets/images/icons/Shield.svg';
import { ReactComponent as StatsIcon } from '../../../assets/images/icons/Stats.svg';

import './index.scss';

export interface EvaluationExercise {
	config: Edge.Models.ExerciseConfiguration;
	metadata: ExerciseMetadata;
	skillId: string;
}

export interface EvaluationProps {
	currentOrganization?: Edge.Models.Organization;
	currentTeam?: Edge.Models.Team;
	exercises: EvaluationExercise[];
	plan: Edge.Models.Plan;
	setCurrentTeamFromCache: (
		currentOrganizationId: string,
		hasCurrentTeam: boolean,
		teams?: Edge.Models.Team[]
	) => void;
	teams?: Edge.Models.Team[];
}

interface EvaluationState {
	selectedSkillId?: string;
}

const DELIVERABLES = [
	{ Icon: NoteIcon, label: 'Overall Edge Score' },
	{ Icon: DifficultyIcon, label: 'Skill Breakdown' },
	{ Icon: StatsIcon, label: 'Performance Insights' },
	{ Icon: NotebookIcon, label: 'Personalized Action Plan' },
];

export class Evaluation extends React.PureComponent<EvaluationProps, EvaluationState> {
	constructor(props: EvaluationProps) {
		super(props);
		this.state = {};
	}

	public componentDidMount() {
		this.setCurrentTeamFromCache();
	}

	public componentDidUpdate() {
		this.setCurrentTeamFromCache();
	}

	public render() {
		const { plan, currentTeam } = this.props;
		return (
			<PlanPageBase
				title="Your Evaluation"
				operationName="Evaluation"
				hideAreaNav
				currentTeam={currentTeam}
				banner={<CanEvalTeamsBanner />}
				plan={plan}
				planPhase={plan.phases[0]}
				renderPlan={this.renderBody}
				renderResults={this.renderResults}
			/>
		);
	}

	private renderResults = () => {
		return <CompleteEvaluation />;
	};

	private renderBody = (trainNow: () => void) => {
		const { exercises, plan } = this.props;

		return (
			<div className="evaluation">
				<h1 className="evaluation_title">Your Edge Test</h1>

				<div className="evaluation_middle">
					<div className="evaluation_panel evaluation_snapshot">
						<div className="evaluation_panel_heading">
							<StatsIcon />
							<h2 className="evaluation_section_label">Your Performance Snapshot</h2>
						</div>
						<p className="evaluation_snapshot_text">
							This Edge Test provides a snapshot of your current visual and cognitive performance across
							six essential skills. Your results help identify strengths, opportunities for improvement
							and changes over time.
						</p>
						<h2 className="evaluation_section_label evaluation_deliverables_label">You&apos;ll Receive</h2>
						<div className="evaluation_deliverables">
							{DELIVERABLES.map(({ Icon, label }) => (
								<div key={label} className="evaluation_deliverable">
									<div className="evaluation_deliverable_icon">
										<Icon />
									</div>
									<span>{label}</span>
								</div>
							))}
						</div>
					</div>

					<div className="evaluation_panel evaluation_skills_panel">
						<h2 className="evaluation_section_label">The Six Skills Measured by the Edge Test</h2>
						<p className="evaluation_skills_hint">Click on each skill to learn more.</p>
						<div className="evaluation_skill_list">
							{exercises.map((exercise, index) => this.renderSkillRow(exercise, index))}
						</div>
					</div>
				</div>

				<section className="evaluation_before">
					<h2 className="evaluation_section_label">Before You Begin</h2>
					<div className="evaluation_before_content">
						<div className="evaluation_before_items">
							<div className="evaluation_before_item">
								<div className="evaluation_before_icon evaluation_before_icon_clock">
									<ClockIcon />
								</div>
								<div>
									<h4>Time Commitment</h4>
									<p>Approximately 10-15 minutes</p>
								</div>
							</div>
							<div className="evaluation_before_item">
								<div className="evaluation_before_icon evaluation_before_icon_equipment">
									<GlassesIcon />
									<LaptopIcon />
								</div>
								<div>
									<h4>Equipment Required</h4>
									<p>
										Vizual Edge 3D glasses + any computer or tablet. Remember, Vizual Edge cannot be
										done on a cell phone.
									</p>
								</div>
							</div>
							<div className="evaluation_before_item">
								<div className="evaluation_before_icon evaluation_before_icon_environment">
									<EnvironmentIcon />
								</div>
								<div>
									<h4>Environment</h4>
									<p>Quiet space, comfortable seat and normal lighting.</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<div className="evaluation_fda_banner">
					<div className="evaluation_fda_icon">
						<ShieldIcon />
					</div>
					<p>
						<strong>NOTE:</strong> These products are not medical devices and have not been reviewed by the
						FDA.
					</p>
				</div>

				<div className="evaluation_cta">
					<StartButton
						onClick={trainNow}
						planPhase={plan.phases[0]}
						startLabel="Begin Edge Test"
						resumeLabel="Edge Test"
					/>
				</div>

				<JsonDebug data={plan} />
			</div>
		);
	};

	private renderSkillRow = ({ metadata, skillId }: EvaluationExercise, index: number) => {
		const isSelected = this.state.selectedSkillId === skillId;

		return (
			<div key={skillId} className="evaluation_skill_row_wrapper">
				<button
					type="button"
					className={`evaluation_skill_row${isSelected ? ' is_selected' : ''}`}
					onClick={() => this.toggleSkill(skillId)}
				>
					<span className="evaluation_skill_number">{index + 1}</span>
					<span className="evaluation_skill_icon">
						<metadata.logo />
					</span>
					<span className="evaluation_skill_content">
						{isSelected ? (
							<span className="evaluation_skill_description">{metadata.description}</span>
						) : (
							<span className="evaluation_skill_name">{metadata.title}</span>
						)}
					</span>
					<span className="evaluation_skill_chevron" />
				</button>
			</div>
		);
	};

	private toggleSkill = (skillId: string) => {
		this.setState((state) => ({
			selectedSkillId: state.selectedSkillId === skillId ? undefined : skillId,
		}));
	};

	private setCurrentTeamFromCache = () => {
		const { currentOrganization, currentTeam, setCurrentTeamFromCache, teams } = this.props;
		setCurrentTeamFromCache(currentOrganization ? currentOrganization.id : '', !!currentTeam, teams);
	};
}

function mapStateToProps(state: AppState) {
	const { currentOrganization } = state.organizationState;
	return {
		currentOrganization,
		...getTeamsUserCanEval(state),
	};
}

export default connect(
	mapStateToProps,
	{
		setCurrentTeamFromCache,
	}
)(
	withLoadDataDefaultConfig(
		errorLoadingWrapperHOC(Evaluation),
		() => {},
		async () => {
			const plan = await PlanService.getEvaluationPlan();
			const configs = plan.phases[0].exerciseConfigurations;
			const exercises = [...configs]
				.sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
				.reduce<EvaluationExercise[]>((result, config, index) => {
					const metadata = ExerciseMetadataService.getMetadata(config);
					if (!metadata) {
						return result;
					}

					result.push({
						config,
						metadata,
						skillId: config.id || String(index),
					});
					return result;
				}, []);

			return { plan, exercises };
		}
	)
);
